import { t } from "@hakili/i18n";
import {
  HakiliMark,
  initials,
  RAIL_ACTIVE,
  RAIL_ENTRY,
  RAIL_FOOT_BAR,
  RailAvatarMenu,
  RAIL_FRAME,
  RAIL_IDLE,
  RAIL_NAV,
} from "@hakili/ui";
import { ClipboardList, FileText, LogOut, Users, Wallet } from "lucide-react";
import type { ReactNode } from "react";

/**
 * `round C5-2`, `M1`: **the shell by role , the drawing's two surfaces of
 * stage 2** (`hakili-expert.jsx`, « Étape 2 · le bureau » and « Étape 2 ·
 * l'expert »).
 *
 * The responsable works at a DESK: the rail on the left as the console's and
 * the dealership's (`RailBureau`: « Tournée du jeudi », « Rapports »,
 * « Honoraires », « Mon cabinet »), the content on the right. The field expert
 * works on a PHONE: a header naming who is signed in, the content, and the
 * tour alone , *« L'expert a DEUX onglets et pas quatre : les honoraires du
 * cabinet et son organisation restent au bureau »*; of the drawing's two,
 * « Mes rapports » is the history under `voirHistoriqueCabinet`, which the
 * founder's word (`OD-32`) keeps from the field, so the phone carries « Ma
 * tournée » alone and reads each vehicle's state on it , flagged in the
 * addendum.
 *
 * **« Se déconnecter » on every screen** (6.2): in the rail's foot at the
 * desk, in the header on the phone. The rail says WHO is signed in , *« dans
 * un cabinet à trois, deux personnes ouvrent le même espace et doivent se
 * reconnaître »*.
 */

export type DeskScreen = "tour" | "reports" | "fees" | "cabinet";

export const DESK_SCREENS: readonly {
  readonly id: DeskScreen;
  readonly label: string;
  readonly icon: ReactNode;
}[] = [
  {
    id: "tour",
    label: "expert.rail.tournee",
    icon: <ClipboardList className="h-5 w-5" aria-hidden="true" />,
  },
  { id: "reports", label: "expert.rail.rapports", icon: <FileText className="h-5 w-5" aria-hidden="true" /> },
  { id: "fees", label: "expert.rail.honoraires", icon: <Wallet className="h-5 w-5" aria-hidden="true" /> },
  { id: "cabinet", label: "expert.rail.cabinet", icon: <Users className="h-5 w-5" aria-hidden="true" /> },
];

// Wire values (role codes), keyed as strings , `hakili/no-french-identifier`'s first answer.
const ROLE_LABEL: ReadonlyMap<string, string> = new Map([
  ["responsableCabinet", "expert.role.responsableCabinet"],
  ["expertTerrain", "expert.role.expertTerrain"],
]);

export const roleWords = (role: string): string => {
  const key = ROLE_LABEL.get(role);
  // A role this build has not met is rendered as its own word, never hidden.
  return key === undefined ? role : t(key);
};

export function DeskShell(props: {
  readonly fullName: string;
  readonly role: string;
  readonly screen: DeskScreen;
  readonly onScreen: (screen: DeskScreen) => void;
  /** A screen a later movement builds: greyed, the round in its title (the CN law). */
  readonly greyed?: Readonly<Partial<Record<DeskScreen, string>>>;
  readonly deconnecter: () => void;
  readonly children: ReactNode;
}): React.JSX.Element {
  const { fullName, role, screen, onScreen, greyed = {}, deconnecter, children } = props;
  const title = `${fullName} · ${roleWords(role)}`;
  return (
    <div className="flex min-h-screen bg-gray-50 text-gray-900 antialiased">
      <aside className={RAIL_FRAME}>
        <div className="mb-8 shrink-0">
          <HakiliMark />
        </div>
        <nav className={RAIL_NAV} aria-label="rail">
          {DESK_SCREENS.map((entry) => {
            const active = entry.id === screen;
            const reason = greyed[entry.id];
            return (
              <button
                key={entry.id}
                type="button"
                data-rail={entry.id}
                data-testid={`rail-${entry.id}`}
                aria-current={active ? "page" : undefined}
                disabled={reason !== undefined}
                title={reason === undefined ? t(entry.label) : t("expert.rail.bientot")}
                onClick={() => {
                  onScreen(entry.id);
                }}
                className={`${RAIL_ENTRY} ${
                  reason !== undefined ? "cursor-not-allowed text-gray-300" : active ? RAIL_ACTIVE : RAIL_IDLE
                }`}
              >
                {entry.icon}
                <span className="px-1 text-center text-xs font-medium leading-tight">{t(entry.label)}</span>
              </button>
            );
          })}
        </nav>
        <div className={RAIL_FOOT_BAR}>
          {/* `D-1019-1`: the avatar carries the way out; the desk has no profile screen. */}
          <RailAvatarMenu
            initials={initials(fullName)}
            name={title}
            tone="bg-indigo-100 text-indigo-700"
            testId="rail-personne"
            wide
            items={[{ label: t("expert.session.deconnexion"), testId: "deconnexion", onSelect: deconnecter }]}
          />
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-5xl px-8 py-7">{children}</div>
      </main>
    </div>
  );
}

export function PhoneShell(props: {
  readonly fullName: string;
  readonly deconnecter: () => void;
  readonly children: ReactNode;
}): React.JSX.Element {
  const { fullName, deconnecter, children } = props;
  return (
    <div className="flex min-h-screen flex-col bg-gray-50 text-gray-900 antialiased">
      <header className="flex shrink-0 items-center gap-2 border-b border-gray-100 bg-white px-5 py-3">
        <HakiliMark />
        <span className="text-base font-bold tracking-tight">hakili</span>
        <span className="ml-auto truncate text-xs text-gray-400" data-testid="entete-personne">
          {fullName}
        </span>
        <button
          type="button"
          onClick={deconnecter}
          title={t("expert.session.deconnexion")}
          aria-label={t("expert.session.deconnexion")}
          data-testid="deconnexion"
          className="ml-2 text-gray-400 hover:text-gray-900"
        >
          <LogOut className="h-5 w-5" aria-hidden="true" />
        </button>
      </header>
      <div className="mx-auto w-full max-w-xl flex-1">{children}</div>
      <nav className="flex shrink-0 border-t border-gray-100 bg-white" aria-label="onglets">
        <span
          className="flex flex-1 flex-col items-center gap-0.5 py-2.5 text-indigo-600"
          data-testid="onglet-tournee"
        >
          <ClipboardList className="h-5 w-5" aria-hidden="true" />
          <span className="text-[11px] font-medium leading-tight">{t("expert.onglet.tournee")}</span>
        </span>
      </nav>
    </div>
  );
}
